import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api, API_BASE } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { EPCBadge } from "../components/EPCBadge";
import { Skeleton } from "../components/ui/skeleton";
import { Checkbox } from "../components/ui/checkbox";
import { TargetListsPanel } from "../components/TargetListsPanel";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Search, FilePlus, RefreshCw, Loader2, ChevronLeft, ChevronRight, ChevronDown, FileText, X, Download, Zap, ShieldCheck, ShieldAlert, ShieldOff, ShieldQuestion } from "lucide-react";
import { toast } from "sonner";
import PropertySheet from "../components/PropertySheet";
import { PriorityBadge, PRIORITY_OPTIONS } from "../components/PriorityBadge";

const BANDS = ["A","B","C","D","E","F","G","UNKNOWN"];
const SAVED_PRESETS = [
  { band: "D", cohort: "not_in_asana", slug: "d-only-not-asana", label: "D only · not in Asana", activeClass: "bg-amber-600 hover:bg-amber-700" },
  { band: "E_OR_BELOW", cohort: "not_in_asana", slug: "e-or-below-not-asana", label: "E or below · not in Asana", activeClass: "bg-red-700 hover:bg-red-800" },
  { band: "ALL", cohort: "post_works_epc", slug: "post-works-epc", label: "Check post-works EPC", activeClass: "bg-sky-700 hover:bg-sky-800" },
];

export default function PropertiesPage() {
  const { user } = useAuth();
  const role = user?.role || "viewer";
  const canEdit = ["super_admin", "admin", "manager", "editor"].includes(role);
  const canImport = ["super_admin", "admin", "manager"].includes(role);
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [search, setSearch] = useState("");
  const [band, setBand] = useState(searchParams.get("band") || "ALL");
  const [priority, setPriority] = useState("ALL");
  const [expiredOnly, setExpiredOnly] = useState(searchParams.get("expired") === "1" || searchParams.get("expired_only") === "true");
  const [expiring12m, setExpiring12m] = useState(searchParams.get("expiring_12m") === "true");
  const [cohort, setCohort] = useState(searchParams.get("cohort") || "ALL");
  const [surveyState, setSurveyState] = useState(searchParams.get("survey_state") || "ALL");
  const [heritagePending, setHeritagePending] = useState(searchParams.get("heritage_pending") === "true");
  const [heritageState, setHeritageState] = useState(searchParams.get("heritage_state") || "ALL");
  const [hideInPlan, setHideInPlan] = useState(true);
  const [targetList, setTargetList] = useState(null);
  const viewTargetList = (key) => {
    setTargetList(key);
    setBand("ALL"); setCohort("ALL"); setPriority("ALL"); setExpiredOnly(false); setExpiring12m(false);
    setSurveyState("ALL"); setHeritagePending(false); setHeritageState("ALL"); setSearch("");
    setPage(1); setSearchParams({});
  };
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [checking, setChecking] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [bulkSelected, setBulkSelected] = useState(new Set());
  const [bulkRunning, setBulkRunning] = useState(false);
  const fileRef = useRef(null);

  const toggleBulkRow = (id) => {
    setBulkSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const togglePageAll = () => {
    setBulkSelected((prev) => {
      const allOnPage = items.every((p) => prev.has(p.id));
      const next = new Set(prev);
      if (allOnPage) {
        items.forEach((p) => next.delete(p.id));
      } else {
        items.forEach((p) => next.add(p.id));
      }
      return next;
    });
  };

  const clearBulk = () => setBulkSelected(new Set());

  const runBulkCheck = async () => {
    if (bulkSelected.size === 0) return;
    setBulkRunning(true);
    try {
      const { data } = await api.post("/properties/bulk-check", { property_ids: Array.from(bulkSelected) });
      toast.success(`Queued ${data.queued} properties for register check`, {
        description: "Alerts and updated bands will appear shortly.",
      });
      clearBulk();
      setTimeout(load, 4000);
    } catch (e) {
      toast.error("Bulk check failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setBulkRunning(false);
    }
  };

  const runBulkSyncGov = async () => {
    if (bulkSelected.size === 0) return;
    setBulkRunning(true);
    try {
      const { data } = await api.post("/properties/bulk-sync-gov", { property_ids: Array.from(bulkSelected) });
      toast.success(`Queued ${data.queued} properties for full gov sync`, {
        description: "Full certificate detail will populate over the next few minutes.",
      });
      clearBulk();
      setTimeout(load, 6000);
    } catch (e) {
      toast.error("Bulk sync failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setBulkRunning(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (search) params.search = search;
      if (band && band !== "ALL") params.band = band;
      if (priority && priority !== "ALL") params.priority = priority;
      if (expiredOnly) params.expired_only = true;
      if (expiring12m) params.expiring_12m = true;
      if (cohort && cohort !== "ALL") params.cohort = cohort;
      if (surveyState && surveyState !== "ALL") params.survey_state = surveyState;
      if (heritagePending) params.heritage_pending = true;
      if (heritageState && heritageState !== "ALL") params.heritage_state = heritageState;
      if (hideInPlan) params.hide_in_plan = true;
      if (targetList) params.target_list = targetList;
      const { data } = await api.get("/properties", { params });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      toast.error("Failed to load properties");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, band, priority, expiredOnly, expiring12m, cohort, surveyState, heritagePending, heritageState, hideInPlan, targetList]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const b = searchParams.get("band");
    if (b && b !== band) setBand(b);
    const c = searchParams.get("cohort");
    if (c && c !== cohort) setCohort(c);
    setExpiredOnly(searchParams.get("expired") === "1" || searchParams.get("expired_only") === "true");
    setExpiring12m(searchParams.get("expiring_12m") === "true");
  }, [searchParams]); // eslint-disable-line

  const onSearch = (e) => {
    e.preventDefault();
    setPage(1);
    load();
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/properties/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.inserted} new, updated ${data.updated}`);
      load();
    } catch (err) {
      toast.error("Import failed", { description: err?.response?.data?.detail || err.message });
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  const triggerCheckAll = async () => {
    setChecking(true);
    try {
      const { data } = await api.post("/properties/check-all", null, { params: { limit: 200 } });
      toast.success(`Queued ${data.queued} properties — alerts will appear shortly`);
      setTimeout(load, 5000);
    } finally {
      setChecking(false);
    }
  };

  const checkOne = async (id) => {
    try {
      const { data } = await api.post(`/properties/${id}/check`);
      toast.success("Property checked", {
        description: data.alerts_created > 0
          ? `${data.alerts_created} new alert(s) created`
          : "No changes detected",
      });
      load();
    } catch {
      toast.error("Check failed");
    }
  };

  const isPresetActive = (pr) => band === pr.band && cohort === pr.cohort;
  const applyPreset = (pr) => {
    setTargetList(null);
    const on = !isPresetActive(pr);
    setBand(on ? pr.band : "ALL");
    setCohort(on ? pr.cohort : "ALL");
    setPriority("ALL"); setExpiredOnly(false); setExpiring12m(false);
    setSurveyState("ALL"); setHeritagePending(false); setHeritageState("ALL");
    setPage(1);
    setSearchParams(on ? { band: pr.band, cohort: pr.cohort } : {});
  };

  const exportCsv = async (format = "csv", sort) => {
    try {
      // Mirror every active filter on the listing so the CSV matches what
      // the user is looking at. Previously cohort + expired + expiring +
      // Unknown band were ignored, producing too many (or zero) rows.
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (band && band !== "ALL") params.set("band", band);
      if (priority && priority !== "ALL") params.set("priority", priority);
      if (expiredOnly) params.set("expired_only", "true");
      if (expiring12m) params.set("expiring_12m", "true");
      if (cohort && cohort !== "ALL") params.set("cohort", cohort);
      if (surveyState && surveyState !== "ALL") params.set("survey_state", surveyState);
      if (heritagePending) params.set("heritage_pending", "true");
      if (heritageState && heritageState !== "ALL") params.set("heritage_state", heritageState);
      if (hideInPlan) params.set("hide_in_plan", "true");
      if (targetList) params.set("target_list", targetList);
      params.set("format", format);
      if (sort) params.set("sort", sort);
      const token = localStorage.getItem("epc_token");
      const res = await fetch(`${API_BASE}/properties/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || "export failed");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers.get("content-disposition") || "";
      const m = cd.match(/filename="?([^";]+)"?/);
      a.download = m ? m[1] : `epc-properties.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} exported`);
    } catch (e) {
      toast.error("Export failed", { description: e.message });
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="p-8 space-y-6 fade-up">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Database</div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight mt-1">Properties</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {total.toLocaleString("en-GB")} records · search, filter, import & check against the EPC register
          </p>
        </div>
        <div className="flex gap-2">
          <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={onImport} data-testid="import-file-input" />
          <Button
            variant="outline"
            onClick={() => exportCsv("csv")}
            className="rounded-none border-slate-300"
            data-testid="properties-export-btn"
          >
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
          <Button
            variant="outline"
            onClick={() => exportCsv("xlsx")}
            className="rounded-none border-slate-300"
            data-testid="properties-export-xlsx-btn"
          >
            <Download className="h-4 w-4 mr-2" /> Export XLSX
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="rounded-none border-slate-300" data-testid="properties-export-pdf-btn">
                <FileText className="h-4 w-4 mr-2" /> Export PDF <ChevronDown className="h-3.5 w-3.5 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-none">
              <DropdownMenuItem onClick={() => exportCsv("pdf", "priority")} data-testid="export-pdf-sort-priority">
                Sorted by retrofit priority (highest first)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportCsv("pdf", "postcode")} data-testid="export-pdf-sort-postcode">
                Grouped by postcode (visit planning)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {canImport && (
            <Button
              variant="outline"
              onClick={() => fileRef.current?.click()}
              disabled={importing}
              className="rounded-none border-slate-300"
              data-testid="properties-import-btn"
            >
              {importing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FilePlus className="h-4 w-4 mr-2" />}
              Import CSV
            </Button>
          )}
          {canEdit && (
            <Button
              onClick={triggerCheckAll}
              disabled={checking}
              className="rounded-none text-white"
              style={{ backgroundColor: "var(--hi-olive)" }}
              data-testid="properties-check-all-btn"
            >
              {checking ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Check All
            </Button>
          )}
        </div>
      </div>

      <TargetListsPanel activeKey={targetList} onView={viewTargetList} />

      <div className="flex flex-wrap items-center gap-3" data-testid="saved-filters-row">
        <label className="flex items-center gap-2 text-sm font-semibold mr-4 cursor-pointer select-none" data-testid="hide-in-plan-toggle">
          <Checkbox checked={hideInPlan} onCheckedChange={(v) => { setHideInPlan(!!v); setPage(1); }} data-testid="hide-in-plan-checkbox" className="rounded-none" />
          Hide properties already in plan
          <span className="text-xs font-normal text-muted-foreground">(Saffron Asana Y1/Y2 · Wave 3)</span>
        </label>
        <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Saved filters</span>
        {SAVED_PRESETS.map((pr) => {
          const active = isPresetActive(pr);
          return (
            <Button
              key={pr.slug}
              type="button"
              variant={active ? "default" : "outline"}
              onClick={() => applyPreset(pr)}
              className={`rounded-none h-9 ${active ? `${pr.activeClass} text-white` : "border-slate-300"}`}
              data-testid={`preset-${pr.slug}`}
            >
              {pr.label}
              {active && <span className="ml-2 text-xs font-mono" data-testid={`preset-${pr.slug}-count`}>({total.toLocaleString("en-GB")})</span>}
            </Button>
          );
        })}
      </div>

      {/* Filter bar */}
      <Card className="rounded-none border-border p-4">
        <form onSubmit={onSearch} className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[260px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by address, postcode, UPRN, town..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="rounded-none border-slate-300 pl-9 h-10"
              data-testid="properties-search"
            />
            {search && (
              <button type="button" onClick={() => { setSearch(""); setPage(1); }} className="absolute right-3 top-1/2 -translate-y-1/2">
                <X className="h-4 w-4 text-muted-foreground" />
              </button>
            )}
          </div>

          <Select value={band} onValueChange={(v) => { setBand(v); setPage(1); setSearchParams(v === "ALL" ? {} : { band: v }); }}>
            <SelectTrigger className="w-44 rounded-none border-slate-300 h-10" data-testid="filter-band">
              <SelectValue placeholder="All Bands" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Bands</SelectItem>
              <SelectItem value="E_OR_BELOW">E or below (E/F/G)</SelectItem>
              {BANDS.map((b) => (
                <SelectItem key={b} value={b}>Band {b}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={priority} onValueChange={(v) => { setPriority(v); setPage(1); }}>
            <SelectTrigger className="w-56 rounded-none border-slate-300 h-10" data-testid="filter-priority">
              <SelectValue placeholder="All Priorities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Retrofit Priorities</SelectItem>
              {PRIORITY_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            type="button"
            variant={expiredOnly ? "default" : "outline"}
            onClick={() => { setExpiredOnly((v) => !v); setExpiring12m(false); setPage(1); setSearchParams(!expiredOnly ? { expired_only: "true" } : {}); }}
            className={`rounded-none h-10 ${expiredOnly ? "bg-red-700 hover:bg-red-800 text-white" : "border-slate-300"}`}
            data-testid="filter-expired"
          >
            Expired only
          </Button>

          <Button
            type="button"
            variant={expiring12m ? "default" : "outline"}
            onClick={() => { setExpiring12m((v) => !v); setExpiredOnly(false); setPage(1); setSearchParams(!expiring12m ? { expiring_12m: "true" } : {}); }}
            className="rounded-none h-10"
            style={expiring12m ? { backgroundColor: "var(--hi-orange)", color: "white", borderColor: "var(--hi-orange)" } : undefined}
            data-testid="filter-expiring-12m"
          >
            Expiring in 12m
          </Button>

          <Select value={cohort} onValueChange={(v) => { setCohort(v); setPage(1); }}>
            <SelectTrigger className="w-[210px] rounded-none border-slate-300 h-10" data-testid="cohort-filter">
              <SelectValue placeholder="All cohorts" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All cohorts</SelectItem>
              <SelectItem value="not_in_asana">Not in Asana (incl. Y2 Standby)</SelectItem>
              <SelectItem value="post_works_epc">Check post-works EPC</SelectItem>
              <SelectItem value="none">Backlog (no programme)</SelectItem>
              <SelectItem value="any">Any programme</SelectItem>
              <SelectItem value="wave3-y1">Wave 3 · Year 1 (delivered)</SelectItem>
              <SelectItem value="wave3-y2">Wave 3 · Year 2 (scheduled)</SelectItem>
              <SelectItem value="wave3-y2-standby">Wave 3 · Year 2 · Standby</SelectItem>
            </SelectContent>
          </Select>

          <Select value={surveyState} onValueChange={(v) => { setSurveyState(v); setPage(1); }}>
            <SelectTrigger className="w-[200px] rounded-none border-slate-300 h-10" data-testid="survey-state-filter">
              <SelectValue placeholder="All survey states" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All survey states</SelectItem>
              <SelectItem value="not_attempted">Not attempted</SelectItem>
              <SelectItem value="attempted_no_contact">Attempted · No contact</SelectItem>
              <SelectItem value="refused">Resident refused</SelectItem>
              <SelectItem value="vacant">Vacant</SelectItem>
              <SelectItem value="surveyed">Surveyed</SelectItem>
            </SelectContent>
          </Select>

          <Select value={heritageState} onValueChange={(v) => { setHeritageState(v); setPage(1); }}>
            <SelectTrigger className="w-[200px] rounded-none border-slate-300 h-10" data-testid="heritage-state-filter">
              <SelectValue placeholder="Any heritage" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Any heritage state</SelectItem>
              <SelectItem value="clear">Clear · no constraints</SelectItem>
              <SelectItem value="consent">Consent likely</SelectItem>
              <SelectItem value="blocked">Consent REQUIRED (block)</SelectItem>
              <SelectItem value="has_constraints">Any constraints</SelectItem>
              <SelectItem value="pending">Pending / unscanned</SelectItem>
            </SelectContent>
          </Select>

          <Button
            type="button"
            variant={heritagePending ? "default" : "outline"}
            onClick={() => {
              setHeritagePending((v) => !v);
              setPage(1);
              // Update URL so the filter survives a refresh / share link
              const next = new URLSearchParams(searchParams);
              if (!heritagePending) next.set("heritage_pending", "true");
              else next.delete("heritage_pending");
              setSearchParams(next);
            }}
            className="rounded-none h-10"
            style={heritagePending ? { backgroundColor: "#475569", color: "white", borderColor: "#475569" } : undefined}
            data-testid="filter-heritage-pending"
            title="Show properties whose planning-data scan hasn't completed yet"
          >
            Heritage pending
          </Button>

          <Button type="submit" variant="outline" className="rounded-none border-slate-300 h-10" data-testid="apply-filters">
            Apply
          </Button>
        </form>
      </Card>

      {/* Bulk action bar */}
      {bulkSelected.size > 0 && (
        <Card className="rounded-none p-3 flex items-center justify-between" style={{ borderColor: "var(--hi-olive)", backgroundColor: "var(--hi-cream)", border: "2px solid" }} data-testid="bulk-toolbar">
          <div className="text-sm font-semibold" style={{ color: "var(--hi-olive)" }}>
            {bulkSelected.size} {bulkSelected.size === 1 ? "property" : "properties"} selected
          </div>
          <div className="flex items-center gap-2">
            {canEdit && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={runBulkCheck}
                  disabled={bulkRunning}
                  className="rounded-none border-slate-300"
                  data-testid="bulk-check-btn"
                >
                  {bulkRunning ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                  Quick check ({bulkSelected.size})
                </Button>
                <Button
                  size="sm"
                  onClick={runBulkSyncGov}
                  disabled={bulkRunning}
                  className="rounded-none text-white"
                  style={{ backgroundColor: "var(--hi-olive)" }}
                  data-testid="bulk-sync-btn"
                >
                  {bulkRunning ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                  Full gov sync ({bulkSelected.size})
                </Button>
              </>
            )}
            <Button size="sm" variant="ghost" onClick={clearBulk} className="rounded-none h-8" data-testid="bulk-clear-btn">
              <X className="h-3.5 w-3.5 mr-1" /> Clear
            </Button>
          </div>
        </Card>
      )}

      {/* Table */}
      <Card className="rounded-none border-border overflow-hidden">
        <div className="overflow-auto">
          <Table>
            <TableHeader className="bg-slate-50 sticky top-0">
              <TableRow className="border-border">
                <TableHead className="w-10 text-center">
                  <input
                    type="checkbox"
                    checked={items.length > 0 && items.every((p) => bulkSelected.has(p.id))}
                    onChange={togglePageAll}
                    className="rounded-none cursor-pointer accent-slate-700"
                    data-testid="bulk-toggle-all"
                  />
                </TableHead>
                <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-700">Address</TableHead>
                <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-700">Postcode</TableHead>
                <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-700">UPRN</TableHead>
                <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-700 text-center">Previous</TableHead>
                <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-700 text-center">Current</TableHead>
                <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-700 text-center">Expiry</TableHead>
                <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-700">SAP</TableHead>
                <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-700"><div className="flex items-center gap-1"><Zap className="h-3 w-3" />Retrofit</div></TableHead>
                <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-700 text-center"><div className="flex items-center justify-center gap-1"><ShieldCheck className="h-3 w-3" />Heritage</div></TableHead>
                <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-700">Heating</TableHead>
                <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-700 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [...Array(8)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={12}><Skeleton className="h-6" /></TableCell>
                  </TableRow>
                ))
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={11} className="text-center py-16 text-muted-foreground">No properties match your filters.</TableCell></TableRow>
              ) : items.map((p) => {
                const prev = p.previous_epc || {};
                const cur = p.current_epc || {};
                const changed = prev.band && cur.band && prev.band !== cur.band;
                const isChecked = bulkSelected.has(p.id);
                return (
                  <TableRow
                    key={p.id}
                    className={`hover:bg-slate-50 cursor-pointer border-border group ${isChecked ? "bg-amber-50/40" : ""}`}
                    onClick={() => setSelectedId(p.id)}
                    data-testid={`property-row-${p.id}`}
                  >
                    <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleBulkRow(p.id)}
                        className="rounded-none cursor-pointer accent-slate-700"
                        data-testid={`bulk-select-${p.id}`}
                        aria-label={`Select ${p.address}`}
                      />
                    </TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2 flex-wrap">
                        {changed && <span className="h-2 w-2 bg-amber-500 rounded-full" title="Recent change" />}
                        <span className="truncate max-w-xs">{p.address}</span>
                        {p.programme?.cohort === "wave3-y1" && (
                          <span
                            className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border border-emerald-700 text-emerald-800 bg-emerald-50 whitespace-nowrap"
                            title={`Wave 3 Year 1 · ${p.programme.asana_stage || (p.programme.status || "").replace("_", " ")}${p.programme.cph_status ? ` · ${p.programme.cph_status}` : ""}`}
                            data-testid={`cohort-badge-${p.id}`}
                          >
                            W3·Y1
                          </span>
                        )}
                        {p.programme?.cohort === "wave3-y2" && (
                          <span
                            className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border border-sky-700 text-sky-800 bg-sky-50 whitespace-nowrap"
                            title={`Wave 3 Year 2 · ${p.programme.asana_stage || "scheduled"}${p.programme.asana_task_id ? ` · Asana ${p.programme.asana_task_id}` : ""}`}
                            data-testid={`cohort-badge-${p.id}`}
                          >
                            W3·Y2
                          </span>
                        )}
                        {p.programme?.cohort === "saffron-epcs" && (
                          <span
                            className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border border-violet-700 text-violet-800 bg-violet-50 whitespace-nowrap"
                            title={`Saffron Housing EPCs (Asana) · ${p.programme.asana_stage || "open"}`}
                            data-testid={`cohort-badge-${p.id}`}
                          >
                            EPCs
                          </span>
                        )}

                        {p.programme?.asana_stage && (
                          <span className="text-[10px] text-slate-500 whitespace-nowrap" data-testid={`asana-stage-${p.id}`}>{p.programme.asana_stage}</span>
                        )}
                        {p.programme?.asana_url && (
                          <a href={p.programme.asana_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                             className="text-[10px] text-blue-700 underline whitespace-nowrap" data-testid={`asana-link-${p.id}`}>Asana ↗</a>
                        )}
                        {p.programme?.cohort === "wave3-y2-standby" && (
                          <span
                            className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border border-violet-700 text-violet-800 bg-violet-50 whitespace-nowrap"
                            title="Wave 3 Year 2 — standby (ready if required, not yet committed)"
                            data-testid={`cohort-badge-${p.id}`}
                          >
                            W3·Y2·S
                          </span>
                        )}
                        {p.survey_status?.state === "attempted_no_contact" && (
                          <span
                            className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border border-amber-500 text-amber-800 bg-amber-50 whitespace-nowrap"
                            title={`Attempted but no contact · ${p.survey_status.attempts || 1} attempt(s)`}
                            data-testid={`survey-badge-${p.id}`}
                          >
                            No Contact · {p.survey_status.attempts || 1}
                          </span>
                        )}
                        {p.survey_status?.state === "refused" && (
                          <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border border-red-600 text-red-800 bg-red-50 whitespace-nowrap" title="Resident refused access" data-testid={`survey-badge-${p.id}`}>
                            Refused
                          </span>
                        )}
                        {p.survey_status?.state === "vacant" && (
                          <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border border-slate-600 text-slate-700 bg-slate-100 whitespace-nowrap" title="Property vacant" data-testid={`survey-badge-${p.id}`}>
                            Vacant
                          </span>
                        )}
                        {p.survey_status?.state === "surveyed" && (
                          <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border border-emerald-600 text-emerald-800 bg-emerald-50 whitespace-nowrap" title="Survey complete" data-testid={`survey-badge-${p.id}`}>
                            Surveyed
                          </span>
                        )}
                      </div>
                      {p.town && <div className="text-xs text-muted-foreground">{p.town}</div>}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{p.postcode}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{p.uprn}</TableCell>
                    <TableCell className="text-center"><EPCBadge band={prev.band} size="sm" /></TableCell>
                    <TableCell className="text-center"><EPCBadge band={cur.band} size="sm" /></TableCell>
                    <TableCell className="text-center" data-testid={`expiry-${p.id}`}>
                      {cur.expired ? (
                        <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border border-red-600 text-red-700 bg-red-50">Expired</span>
                      ) : cur.expiry_date ? (
                        <span className="text-[10px] font-mono text-slate-500">{new Date(cur.expiry_date).toLocaleDateString("en-GB")}</span>
                      ) : (
                        <span className="text-[10px] text-slate-300">—</span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{cur.sap_rating ?? "—"}</TableCell>
                    <TableCell><PriorityBadge priority={p.retrofit?.priority} score={p.retrofit?.score} /></TableCell>
                    <TableCell className="text-center">
                      <HeritageCell heritage={p.heritage} />
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground truncate max-w-[140px]">{p.space_heating_system || "—"}</TableCell>
                    <TableCell className="text-right">
                      {canEdit && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="rounded-none opacity-0 group-hover:opacity-100 transition-opacity h-7 px-2"
                          onClick={(e) => { e.stopPropagation(); checkOne(p.id); }}
                          data-testid={`check-property-${p.id}`}
                        >
                          <RefreshCw className="h-3 w-3 mr-1" /> Check
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {!loading && total > pageSize && (
          <div className="flex items-center justify-between p-4 border-t border-border bg-slate-50">
            <div className="text-xs text-muted-foreground font-mono">
              Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total.toLocaleString("en-GB")}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="rounded-none border-slate-300" disabled={page === 1} onClick={() => setPage(p => p - 1)} data-testid="page-prev">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="text-xs font-mono px-3">Page {page} / {totalPages}</div>
              <Button variant="outline" size="sm" className="rounded-none border-slate-300" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="page-next">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      <PropertySheet propertyId={selectedId} onClose={() => setSelectedId(null)} onChanged={load} />
    </div>
  );
}


/* -------------------------------------------------------------------------
 * Inline heritage cell on the properties list — at-a-glance "does this need
 * planning?" so the operator can scan-pick jobs without opening every row.
 *
 * Four states:
 *   - pending  (slate) — never scanned OR last scan returned ok=false
 *   - clear    (emerald) — scanned and no constraints
 *   - consent  (amber) — has constraints but none are 'block' severity
 *   - blocked  (red)   — at least one block-severity constraint
 *     (Listed Building, World Heritage, Scheduled Monument, Flood Risk Zone)
 * ------------------------------------------------------------------------- */
const BLOCKING_DATASETS = new Set([
  "listed-building", "world-heritage-site", "scheduled-monument", "flood-risk-zone",
]);

function HeritageCell({ heritage }) {
  if (!heritage || !heritage.ok) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border border-slate-300 text-slate-500 bg-slate-50"
        title="Heritage lookup pending — UK gov planning API not yet checked or last check failed"
        data-testid="heritage-cell-pending"
      >
        <ShieldQuestion className="h-3 w-3" />
        Pending
      </span>
    );
  }
  const constraints = heritage.constraints || [];
  if (!heritage.has_constraints || constraints.length === 0) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border border-emerald-300 text-emerald-700 bg-emerald-50"
        title="Heritage clear — no conservation, listing, AONB, Article-4, flood-zone, green-belt or scheduled-monument constraints"
        data-testid="heritage-cell-clear"
      >
        <ShieldCheck className="h-3 w-3" />
        Clear
      </span>
    );
  }
  const isBlocked = constraints.some(
    (c) => BLOCKING_DATASETS.has(c.dataset) || c.severity === "block",
  );
  const labels = constraints.map((c) => (c.label || c.dataset || "").replace(/-/g, " ")).join(" · ");
  if (isBlocked) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border border-red-500 text-red-800 bg-red-50 whitespace-nowrap"
        title={`Consent REQUIRED — ${labels}`}
        data-testid="heritage-cell-blocked"
      >
        <ShieldOff className="h-3 w-3" />
        Consent
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border border-amber-500 text-amber-800 bg-amber-50 whitespace-nowrap"
      title={`Planning consent likely — ${labels}`}
      data-testid="heritage-cell-consent"
    >
      <ShieldAlert className="h-3 w-3" />
      Check
    </span>
  );
}
